import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  runTransaction,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, default as firebaseApp } from '../firebase';
import { Transaction } from '../types';
import { HoldingsCalculationService } from './holdingsCalculationService';
import { logger } from '../utils/logger';
import {
  calculateBalanceFromTransactions,
  calculateTransactionBalanceEffect,
  resolveInitialBalance,
} from '../utils/balanceCalculation';

/**
 * 取得目前登入使用者的 uid(供 array-contains 查詢使用)
 * 未登入時拋錯 — 查詢無法滿足 rules 的成員檢查
 */
function requireCurrentUid(): string {
  const uid = getAuth(firebaseApp).currentUser?.uid;
  if (!uid) {
    throw new Error('尚未登入,無法查詢交易資料');
  }
  return uid;
}

/**
 * 建立以 allowedUsers 為權限基礎的交易查詢(rules 不是 filter:
 * 查詢必須以 array-contains 表達,否則會被整批拒絕)
 */
function buildAccountTransactionsQuery(accountId: string) {
  const uid = requireCurrentUid();
  return query(
    collection(db, 'transactions'),
    where('accountId', '==', accountId),
    where('allowedUsers', 'array-contains', uid)
  );
}

/** doc → Transaction 轉換(共用 mapper,取代原本重複 7 次的轉換區塊) */
function mapDocToTransaction(
  docSnap: { id: string; data(): Record<string, any> }
): Transaction {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    timestamp: data.timestamp?.toDate() || new Date(),
    transactionDate: data.transactionDate?.toDate
      ? data.transactionDate.toDate()
      : data.transactionDate
        ? new Date(data.transactionDate)
        : undefined,
  } as Transaction;
}

/** 依「實際交易日期」降冪排序(CSV 匯入的歷史交易順序才會正確) */
function sortByTransactionDateDesc(transactions: Transaction[]): Transaction[] {
  return transactions.sort((a, b) => {
    const aTime = (a.transactionDate ?? a.timestamp)?.getTime() ?? 0;
    const bTime = (b.transactionDate ?? b.timestamp)?.getTime() ?? 0;
    return bTime - aTime;
  });
}

export class TransactionService {
  private static readonly COLLECTION_NAME = 'transactions';

  // Add new transaction
  static async addTransaction(
    accountId: string,  // 交易帳戶 ID
    transactionData: Omit<Transaction, 'id' | 'accountId' | 'timestamp'>
  ): Promise<string> {
    try {
      // 如果用戶指定了交易日期，將其轉換為 Date 物件
      const transactionDate = transactionData.transactionDate
        ? new Date(transactionData.transactionDate)
        : null;

      // Clean up undefined values before saving to Firestore
      const cleanTransactionData = Object.fromEntries(
        Object.entries(transactionData).filter(([_, value]) => value !== undefined)
      );

      // 帳戶文件:取得 allowedUsers 標記與餘額(FIN-10:與交易寫入同一原子單元)
      const accountRef = doc(db, 'tradingAccounts', accountId);

      const docRef = await runTransaction(db, async (tx) => {
        const accountSnap = await tx.get(accountRef);
        if (!accountSnap.exists()) {
          throw new Error('Account not found');
        }
        const accountData = accountSnap.data();
        const allowedUsers: string[] = accountData.allowedUsers ?? [];

        // 建立交易文件(在同一 transaction 內,與餘額更新原子化)
        const newTxRef = doc(collection(db, this.COLLECTION_NAME));
        tx.set(newTxRef, {
          ...cleanTransactionData,
          accountId,  // 使用交易帳戶 ID
          allowedUsers,  // 帳號成員清單(rules 限權依據)
          timestamp: serverTimestamp(),  // 系統時間戳（創建時間）
          transactionDate: transactionDate,  // 用戶指定的交易日期
          createdAt: serverTimestamp(),
        });

        // 以單一實作計算餘額效果(FIN-11),讀改寫全程在 transaction 內(FIN-10)
        const currentBalance = accountData.balance ?? 0;
        const effect = calculateTransactionBalanceEffect({
          ...transactionData,
          id: '',
          accountId,
          timestamp: new Date(),
        } as Transaction);
        const newBalance = Math.round(currentBalance + effect);

        tx.update(accountRef, {
          balance: newBalance,
          updatedAt: serverTimestamp(),
        });

        return newTxRef;
      });

      // 持股組合為衍生資料,失敗可重算,不影響帳務正確性
      await this.recalculatePortfolioDoc(accountId);

      return docRef.id;
    } catch (error) {
      logger.transaction.error('Failed to add transaction', error);
      throw new Error('Failed to add transaction');
    }
  }

  // Get all transactions for an account
  static async getUserTransactions(accountId: string): Promise<Transaction[]> {
    // FIN-01:查詢失敗一律拋錯 — 靜默回傳 [] 會讓重算流程把餘額覆寫回初始值
    const q = buildAccountTransactionsQuery(accountId);
    const querySnapshot = await getDocs(q);
    const transactions = querySnapshot.docs.map(mapDocToTransaction);
    return sortByTransactionDateDesc(transactions);
  }

  // Listen to account transactions in real-time
  static subscribeToUserTransactions(
    accountId: string,  // 交易帳戶 ID
    callback: (transactions: Transaction[]) => void
  ): () => void {
    // 驗證輸入
    if (!accountId) {
      throw new Error('accountId cannot be empty');
    }
    if (!callback || typeof callback !== 'function') {
      throw new Error('callback must be a function');
    }

    const q = buildAccountTransactionsQuery(accountId);

    return onSnapshot(q, (querySnapshot) => {
      const transactions = querySnapshot.docs.map(mapDocToTransaction);
      callback(sortByTransactionDateDesc(transactions));
    }, (error) => {
      // FIN-01:監聽錯誤不得回呼空陣列(會讓 UI 呈現「無交易」並可能觸發錯誤重算)
      // 保留最後已知狀態,僅記錄錯誤
      logger.transaction.error('Transaction subscription error', error);
    });
  }

  // Update transaction
  static async updateTransaction(
    transactionId: string,
    updates: Partial<Omit<Transaction, 'id' | 'accountId'>>
  ): Promise<void> {
    try {
      // Get transaction before update to determine account ID
      const originalTransaction = await this.getTransactionById(transactionId);
      if (!originalTransaction) {
        throw new Error('Transaction not found');
      }

      const transactionRef = doc(db, this.COLLECTION_NAME, transactionId);

      // Clean up undefined values before updating Firestore;
      // allowedUsers 為 rules 管理欄位,成員更新時不得變更
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(
          ([key, value]) => value !== undefined && key !== 'allowedUsers'
        )
      );

      await updateDoc(transactionRef, {
        ...cleanUpdates,
        updatedAt: serverTimestamp(),
      });

      // After updating the transaction, recalculate account balance
      await this.updateAccountBalanceAfterBatch(originalTransaction.accountId);
    } catch (error) {
      logger.transaction.error('Failed to update transaction', error);
      throw new Error('Failed to update transaction');
    }
  }

  // Delete transaction
  static async deleteTransaction(transactionId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION_NAME, transactionId));
    } catch (error) {
      logger.transaction.error('Failed to delete transaction', error);
      throw new Error('Failed to delete transaction');
    }
  }

  // Get transaction by ID
  static async getTransactionById(transactionId: string): Promise<Transaction | null> {
    const docRef = doc(db, this.COLLECTION_NAME, transactionId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return mapDocToTransaction(docSnap);
    }
    return null;
  }

  // 使用統一的持股計算服務
  static calculatePortfolioFromTransactions(transactions: Transaction[]) {
    const { currentHoldings } = HoldingsCalculationService.calculateHoldingsFromTransactions(
      transactions,
      [], // 此處不需要股價資料
      { includeFees: true, includeHistorical: false }
    );

    // 轉換為舊格式以保持相容性
    return currentHoldings.map(holding => ({
      symbol: holding.symbol,
      quantity: holding.currentQuantity,
      totalCost: holding.investmentCost,
      avgCost: holding.avgCost,
    })).filter(holding => holding.quantity > 0);
  }

  // Batch import transactions with optimized performance
  static async batchAddTransactions(
    accountId: string,
    transactionDataArray: Array<Omit<Transaction, 'id' | 'accountId' | 'timestamp'>>,
    onProgress?: (progress: { current: number; total: number; currentItem: string }) => void
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    const BATCH_SIZE = 450; // Firestore batch 上限 500,預留餘裕(FIN-13 同型原則)
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      // 讀取帳戶文件一次,取得 allowedUsers 供每筆文件標記
      const accountRef = doc(db, 'tradingAccounts', accountId);
      const accountSnap = await getDoc(accountRef);
      if (!accountSnap.exists()) {
        throw new Error('Account not found');
      }
      const allowedUsers: string[] = accountSnap.data().allowedUsers ?? [];

      for (let i = 0; i < transactionDataArray.length; i += BATCH_SIZE) {
        const chunk = transactionDataArray.slice(i, i + BATCH_SIZE);

        try {
          await this.processBatchChunk(accountId, allowedUsers, chunk, (chunkProgress) => {
            const globalProgress = {
              current: i + chunkProgress.current,
              total: transactionDataArray.length,
              currentItem: chunkProgress.currentItem
            };
            onProgress?.(globalProgress);
          });

          result.success += chunk.length;
        } catch (error) {
          logger.transaction.error('Batch chunk failed', error);
          result.failed += chunk.length;
          result.errors.push(`批次 ${Math.floor(i / BATCH_SIZE) + 1} 失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      }

      // Recalculate portfolio after all transactions are imported
      if (result.success > 0) {
        await this.recalculatePortfolioDoc(accountId);
      }

      return result;
    } catch (error) {
      logger.transaction.error('Batch import failed', error);
      throw new Error('批次匯入失敗');
    }
  }

  // Process a chunk of transactions using Firestore batch
  private static async processBatchChunk(
    accountId: string,
    allowedUsers: string[],
    transactions: Array<Omit<Transaction, 'id' | 'accountId' | 'timestamp'>>,
    onProgress?: (progress: { current: number; total: number; currentItem: string }) => void
  ): Promise<void> {
    const batch = writeBatch(db);

    for (let i = 0; i < transactions.length; i++) {
      const transactionData = transactions[i];

      // Progress callback
      onProgress?.({
        current: i + 1,
        total: transactions.length,
        currentItem: `${transactionData.symbol} - ${transactionData.type}`
      });

      // Prepare transaction date
      const transactionDate = transactionData.transactionDate
        ? new Date(transactionData.transactionDate)
        : null;

      // Create new document reference
      const docRef = doc(collection(db, this.COLLECTION_NAME));

      // Clean up undefined values before saving to Firestore
      const cleanTransactionData = Object.fromEntries(
        Object.entries(transactionData).filter(([_, value]) => value !== undefined)
      );

      // Add to batch
      batch.set(docRef, {
        ...cleanTransactionData,
        accountId,
        allowedUsers,
        timestamp: serverTimestamp(),
        transactionDate: transactionDate,
        createdAt: serverTimestamp(),
      });
    }

    // Execute batch write
    await batch.commit();
  }

  /**
   * 重算並寫入 portfolio 文件(衍生資料)
   * @param allowedUsers 傳入時直接標記;未傳入則讀取帳戶文件
   */
  static async recalculatePortfolioDoc(accountId: string, allowedUsers?: string[]): Promise<void> {
    try {
      const allTransactions = await this.getUserTransactions(accountId);
      const holdings = this.calculatePortfolioFromTransactions(allTransactions);

      let members = allowedUsers;
      if (!members) {
        const accountSnap = await getDoc(doc(db, 'tradingAccounts', accountId));
        members = accountSnap.exists() ? (accountSnap.data().allowedUsers ?? []) : [];
      }

      const portfolioRef = doc(db, 'portfolios', accountId);
      const portfolioDoc = await getDoc(portfolioRef);

      if (portfolioDoc.exists()) {
        await updateDoc(portfolioRef, {
          holdings,
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(portfolioRef, {
          accountId,
          allowedUsers: members,
          holdings,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      logger.transaction.error('Failed to recalculate portfolio', error);
      // 持股組合為衍生資料且可由 UI 手動重算,失敗僅記錄不中斷
    }
  }

  // Enhanced batch import with account balance calculation
  static async optimizedBatchImport(
    accountId: string,
    transactionDataArray: Array<Omit<Transaction, 'id' | 'accountId' | 'timestamp'>>,
    onProgress?: (progress: {
      current: number;
      total: number;
      currentItem: string;
      phase: 'validation' | 'import' | 'calculation' | 'complete';
    }) => void
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      // Phase 1: Validation
      onProgress?.({
        current: 0,
        total: transactionDataArray.length,
        currentItem: '驗證交易資料...',
        phase: 'validation'
      });

      // Phase 2: Import transactions
      onProgress?.({
        current: 0,
        total: transactionDataArray.length,
        currentItem: '開始匯入交易...',
        phase: 'import'
      });

      const importResult = await this.batchAddTransactions(
        accountId,
        transactionDataArray,
        (progress) => {
          onProgress?.({
            ...progress,
            phase: 'import'
          });
        }
      );

      result.success = importResult.success;
      result.failed = importResult.failed;
      result.errors = importResult.errors;

      // Phase 3: Calculate account balance
      if (result.success > 0) {
        onProgress?.({
          current: transactionDataArray.length,
          total: transactionDataArray.length,
          currentItem: '更新帳戶餘額...',
          phase: 'calculation'
        });

        await this.updateAccountBalanceAfterBatch(accountId);
      }

      // Phase 4: Complete
      onProgress?.({
        current: transactionDataArray.length,
        total: transactionDataArray.length,
        currentItem: '匯入完成',
        phase: 'complete'
      });

      return result;
    } catch (error) {
      logger.transaction.error('Optimized batch import failed', error);
      result.errors.push(`批次匯入失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      return result;
    }
  }

  // Update account balance after batch import
  static async updateAccountBalanceAfterBatch(accountId: string): Promise<void> {
    // FIN-01:失敗必須拋出 — 靜默吞錯會讓呼叫者以為重算成功
    const accountRef = doc(db, 'tradingAccounts', accountId);
    const accountDoc = await getDoc(accountRef);

    if (!accountDoc.exists()) {
      throw new Error('Account not found');
    }

    const accountData = accountDoc.data();
    // 取得初始餘額(單一實作的向下相容規則)
    const initialBalance = resolveInitialBalance(accountData);

    // Get all transactions and recalculate balance(單一實作 FIN-11)
    const allTransactions = await this.getUserTransactions(accountId);
    const newBalance = calculateBalanceFromTransactions(initialBalance, allTransactions);

    await updateDoc(accountRef, {
      balance: newBalance,
      updatedAt: serverTimestamp(),
      lastBatchUpdate: serverTimestamp(),
    });
  }
}
